import { type JSX, memo } from "react";

import { generatePaginationLinks } from "./GeneratePages";
import type { PaginatorProps } from "./pagination.types";

import {
  FIFTY,
  HUNDRED,
  HUNDRED_FIFTY,
  TEN,
  TWENTY_FIVE,
} from "@/app/constants/constants";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DEFAULT_PAGE_SIZES = [TEN, TWENTY_FIVE, FIFTY, HUNDRED, HUNDRED_FIFTY];

interface PageSizeSelectorProps {
  pageSize: number;
  pageSizeOptions: number[];
  onPageSizeChange: (size: number) => void;
}

/**
 * Dropdown selector for choosing the number of rows displayed per page.
 *
 * @param {PageSizeSelectorProps} props - Page size selector configuration.
 * @returns {JSX.Element} Rendered page size dropdown.
 */
const PageSizeSelector = (props: PageSizeSelectorProps): JSX.Element => {
  const { pageSize, pageSizeOptions, onPageSizeChange } = props;

  return (
    <div className='flex items-center space-x-2'>
      <p className='text-sm font-medium whitespace-nowrap'>Show</p>
      <Select
        value={`${pageSize}`}
        onValueChange={(value) => onPageSizeChange(Number(value))}
      >
        <SelectTrigger className='h-8 w-[5rem]'>
          <SelectValue placeholder={pageSize} />
        </SelectTrigger>
        <SelectContent side='top'>
          {pageSizeOptions.map((size) => (
            <SelectItem key={size} value={`${size}`}>
              {size}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className='text-sm font-medium whitespace-nowrap'>Entries</p>
    </div>
  );
};

/**
 * Generic paginator component — works with any data structure (tables, grids, lists).
 * No dependency on TanStack Table or any specific data source.
 *
 * @param {PaginatorProps} props - Paginator configuration.
 * @returns {JSX.Element} Rendered pagination controls with optional page size selector.
 */
const PaginatorComponent = (props: PaginatorProps): JSX.Element => {
  const {
    currentPage,
    totalPages,
    pageSize,
    pageSizeOptions = DEFAULT_PAGE_SIZES,
    showPreviousNext = true,
    onPageChange,
    onPageSizeChange,
  } = props;

  const clampedPageChange = (pageNumber: number): void => {
    const normalizedPageNumber = Math.min(Math.max(pageNumber, 1), totalPages);

    onPageChange(normalizedPageNumber);
  };

  return (
    <div className='flex items-center gap-4'>
      {onPageSizeChange && (
        <PageSizeSelector
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageSizeChange={onPageSizeChange}
        />
      )}
      <Pagination>
        <PaginationContent>
          {showPreviousNext && totalPages > 0 && (
            <PaginationItem>
              <PaginationPrevious
                onClick={() => clampedPageChange(currentPage - 1)}
                disabled={currentPage <= 1}
              />
            </PaginationItem>
          )}
          {generatePaginationLinks(currentPage, totalPages, clampedPageChange)}
          {showPreviousNext && totalPages > 0 && (
            <PaginationItem>
              <PaginationNext
                onClick={() => clampedPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
              />
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>
    </div>
  );
};

const Paginator = memo(PaginatorComponent);

export default Paginator;
